import { IbanRequestFlow, isIbanIssuingEligible } from '@account'
import { BankIcon } from '@icons'
import { type ReactNode, useContext, useState } from '@integrations/react'
import { useDispatch } from '@integrations/state'
import {
  api,
  selectCurrentUserRole,
  selectMerchantCapabilities,
  useAppSelector,
  useMerchantAccounts,
  useMerchantQry
} from 'mmfintech-backend-api'
import { GlobalContext, tr } from 'mmfintech-commons'
import type { PromoItem } from './PromoBanner'
import StartVerificationModal from './StartVerificationModal'

interface IbanPromo {
  promo: PromoItem | null
  /** The IBAN request wizard, rendered as a sibling of the banner — see the comment below. */
  flow: ReactNode
}

export function useIbanPromo(): IbanPromo {
  const dispatch = useDispatch()
  const { modalShow } = useContext(GlobalContext)
  const customerRole = useAppSelector(selectCurrentUserRole)
  const capabilities = useAppSelector(selectMerchantCapabilities)
  const { merchantLoaded, isMerchantProspect } = useMerchantQry()
  // The dashboard already fetches accounts elsewhere (EnterpriseDashboard's own cards) —
  // refetchOnMountOrArgChange: false so mounting this promo reads the shared cache instead of
  // forcing a second, redundant request for the same data.
  const { paymentAccounts = [], accountsFetching } = useMerchantAccounts({ refetchOnMountOrArgChange: false })

  const [dismissed, setDismissed] = useState(false)
  const [ibanFlowOpen, setIbanFlowOpen] = useState(false)

  const hasEurIban = paymentAccounts.some(
    (account) =>
      account.currencyCode === 'EUR' &&
      account.dedicatedMerchantBankAccounts?.some((dedicated) => Boolean(dedicated?.iban))
  )

  const eligible = isIbanIssuingEligible(customerRole, capabilities)
  // Once opened, the flow's own account/fee queries can flip `accountsFetching` back to true
  // (shared 'Accounts' cache) — it must not be part of the promo's own gate below, or that
  // refetch would unmount the banner (and the modal nested inside it) mid-flow. `ibanFlowOpen` is
  // safe to include here though: it's local state only flipped by explicit user actions (open/
  // close), never by a query re-fetch, so it can't reintroduce that same mount/unmount loop.
  const showPromo = !dismissed && merchantLoaded && !accountsFetching && eligible && !hasEurIban && !ibanFlowOpen

  // IBAN issuing isn't available to prospects (unverified merchants) — they still see the
  // promo (it's a real, near-term benefit worth advertising), but clicking through prompts them
  // to complete verification first instead of opening a wizard they can't actually use.
  function handleIssueIbanClick() {
    if (isMerchantProspect) {
      modalShow({ options: { size: 'medium' }, content: <StartVerificationModal /> })
    } else {
      setIbanFlowOpen(true)
    }
  }

  function handleDismiss() {
    setDismissed(true)
  }

  function handleFlowClose() {
    setIbanFlowOpen(false)
  }

  function reloadAccounts() {
    dispatch(api.util.invalidateTags(['Accounts']))
  }

  const promo: PromoItem | null = showPromo
    ? {
        id: 'issue-iban-banner',
        tone: 'brand',
        icon: <BankIcon />,
        title: tr('FRONTEND.DASHBOARD.ISSUE_IBAN_BANNER.TITLE', 'Get Your Own Named EUR IBAN Now!'),
        text: tr(
          'FRONTEND.DASHBOARD.ISSUE_IBAN_BANNER.SUBTITLE',
          'Send and receive SEPA payments with your own dedicated IBAN.'
        ),
        ctaLabel: tr('FRONTEND.DASHBOARD.ISSUE_IBAN_BANNER.BUTTON', 'Issue IBAN'),
        ctaTestId: 'button-issue-iban-banner',
        dismissTestId: 'issue-iban-banner-close',
        onCta: handleIssueIbanClick,
        onDismiss: handleDismiss
      }
    : null

  const flow = ibanFlowOpen ? <IbanRequestFlow onClose={handleFlowClose} reload={reloadAccounts} /> : null

  return { promo, flow }
}
